import { useEffect, useRef, useState, type FormEvent } from "react";
import s from "./Library.module.css";
import { useAddRevision } from "../../api/queries";
import type { DocumentDetail, DocumentType } from "../../domain/library";
import { Button, Field, Input } from "../../design/components";
import { t } from "../../i18n";

/** Content types `library/access.py` DOCUMENT_TYPES accepts — offered to the file picker, enforced by the platform. */
export const ACCEPTED_DOCUMENT_TYPES =
  ".pdf,.jpg,.jpeg,.png,.tif,.tiff,.docx,.xlsx,.pptx,.doc,.xls,.dwg,.dxf,.txt,.csv,.zip";

/**
 * Draft the next revision of a controlled document, optionally with the
 * file. It lands as a draft — issuing it is a separate act, by a separate
 * person when the type requires approval. An expiring type must be given
 * its expiry here; the platform refuses otherwise, and so does this form.
 */
export function NewRevisionDialog({
  doc,
  type,
  onClose,
  onCreated,
}: {
  doc: DocumentDetail;
  type: DocumentType | undefined;
  onClose: () => void;
  onCreated: (revisionId: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const add = useAddRevision(doc.id);
  const [revision, setRevision] = useState(suggestNext(doc.revisionHistory[0]?.revision));
  const [changeNote, setChangeNote] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [file, setFile] = useState<File | undefined>();

  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    // No close() on cleanup. StrictMode runs this cleanup between its two
    // mounts in development, and the close event it fires unmounted the
    // dialog as it opened — the button did nothing. An unmounted dialog
    // leaves the top layer by itself.
  }, []);

  const needsExpiry = type?.expires ?? false;
  const valid = revision.trim().length > 0 && (!needsExpiry || expiresOn.length > 0);

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid) return;
    add.mutate(
      {
        revision: revision.trim(),
        changeNote: changeNote.trim() || undefined,
        issuedOn: issuedOn || undefined,
        expiresOn: expiresOn || undefined,
        file,
      },
      { onSuccess: (r) => onCreated(r.id) },
    );
  };

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      style={dialogStyle}
      aria-label={t("New revision of {title}", { title: doc.title })}
    >
      <form onSubmit={submit} style={{ display: "grid", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15 }}>{t("New revision — {title}", { title: doc.title })}</h2>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 4 }}>
            {t("Drafted as")} <b>{revision || "…"}</b>
            {t(", sequence {n}.", { n: (doc.revisionHistory[0]?.sequence ?? 0) + 1 })}{" "}
            {type?.requiresApproval
              ? t(
                  "This type needs approval: the draft goes to review, and someone other than the author approves it before it's issued.",
                )
              : t("This type does not require approval: the draft can be issued directly.")}
            {type?.reviewMonths ? ` ${t("Review falls due {n} months after issue.", { n: type.reviewMonths })}` : ""}
          </div>
        </div>

        <Field label="Revision label">
          <Input
            value={revision}
            onChange={(e) => setRevision(e.target.value)}
            aria-label={t("Revision")}
            required
            autoFocus
          />
        </Field>
        <Field label="Change note — what changed and why">
          <textarea
            value={changeNote}
            onChange={(e) => setChangeNote(e.target.value)}
            rows={3}
            style={textareaStyle}
            aria-label={t("Change note")}
          />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Field label="Issued on (leave blank to date it when issued)">
            <Input
              type="date"
              value={issuedOn}
              onChange={(e) => setIssuedOn(e.target.value)}
              aria-label={t("Issued on")}
            />
          </Field>
          <Field label={needsExpiry ? "Expires on (required for this type)" : "Expires on"}>
            <Input
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              aria-label={t("Expires on")}
              required={needsExpiry}
            />
          </Field>
        </div>
        <Field label="File (optional here; a revision without its file can be added later)">
          <input
            type="file"
            accept={ACCEPTED_DOCUMENT_TYPES}
            onChange={(e) => setFile(e.target.files?.[0])}
            aria-label={t("File")}
            style={{ fontSize: 13.5 }}
          />
          {file && (
            <div className="muted" style={{ fontSize: 13 }}>
              {file.name} · {(file.size / 1024).toFixed(0)} KB · {file.type || t("unknown type")}
            </div>
          )}
        </Field>

        {add.isError && (
          <div className={s.refusal} role="alert">
            {t("Refused: {reason}", { reason: (add.error as Error).message })}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <Button onClick={onClose} disabled={add.isPending}>
            {t("Cancel")}
          </Button>
          <Button type="submit" variant="primary" disabled={add.isPending || !valid}>
            {add.isPending ? t("Drafting…") : t("Draft revision")}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

/** "Issue 9" → "Issue 10", "C" → "D", "2.1" → "2.2"; anything else is left for the person. */
export function suggestNext(previous: string | undefined): string {
  if (!previous) return "A";
  const trailingNumber = previous.match(/^(.*?)(\d+)(\D*)$/);
  if (trailingNumber) {
    const [, head, n, tail] = trailingNumber;
    return `${head}${Number(n) + 1}${tail.replace(/\s*\(draft\)\s*$/i, "")}`;
  }
  const letter = previous.match(/^([A-Y])$/);
  if (letter) return String.fromCharCode(letter[1].charCodeAt(0) + 1);
  return "";
}

const dialogStyle: React.CSSProperties = {
  background: "var(--panel)",
  color: "var(--ink)",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius)",
  padding: 20,
  width: "min(600px, 92vw)",
};
const textareaStyle: React.CSSProperties = {
  padding: "6px 10px",
  border: "1px solid var(--line)",
  borderRadius: "var(--radius-sm)",
  background: "var(--panel2)",
  color: "var(--ink)",
  font: "inherit",
  fontSize: 13.5,
};
